// src/api/SubscriptionApi.js
"use strict";

import { api } from "./client";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_DOM_BASE_URL;
const defaultFeatures = ["pos", "inventory"];

/** helper: validate creds */
function requireCreds(creds = {}) {
  const storeId = creds.storeId ?? creds.store_id ?? creds.vendorStoreId;
  const email = creds.email ?? creds.userEmail;
  if (!storeId || !email) throw new Error("Missing storeId/email for subscription request");
  return { storeId, email };
}

/** helper: show toast if backend says subscription is locked */
function showSubscriptionToastIfNeeded(error) {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  const headers = error?.response?.headers || {};
  const hdrActive = headers["x-subscription-active"];
  const hdrReason = headers["x-subscription-lock-reason"];

  const isExpired =
    status === 403 &&
    (data?.code === "SUBSCRIPTION_EXPIRED" ||
      hdrActive === "false" ||
      hdrReason === "subscription_expired");

  if (isExpired) {
    const msg =
      data?.message ||
      "Your POS is in read-only mode because the subscription has expired. Please renew to add, update, or delete.";
    // prevent duplicate toasts if multiple requests fail at once
    try {
      toast.error(msg, { toastId: "subscription-expired" });
    } catch (_) {
      /* toast lib not mounted yet – ignore */
    }
  }
}

/** helper: standard request wrapper */
async function request(promise) {
  try {
    const res = await promise;
    return res.data;
  } catch (err) {
    showSubscriptionToastIfNeeded(err);
    throw err; // keep behavior the same for callers
  }
}

export async function getLatestFeatureSubscription({
  storeId,
  email,
  features = defaultFeatures,
  password,
} = {}) {
  const creds = requireCreds({ storeId, email });
  const body = { ...creds, features };
  if (password) body.password = password; // optional back-compat
  return request(
    api.post(`${API_BASE_URL}api/pos-subscription/get-latest-feature-subscription`, body)
  ); // -> { subscription, isActive, daysLeft, suggestions }
}

export async function previewNextFeatureRenewal({
  storeId,
  email,
  features = defaultFeatures,
} = {}) {
  const creds = requireCreds({ storeId, email });
  return request(
    api.post(`${API_BASE_URL}api/pos-subscription/preview-next-feature-renewal`, {
      ...creds,
      features,
    })
  ); // -> { nextBillingAt, amount, currency, usage, ... }
}

export async function setFeatureAutoRenew({
  storeId,
  email,
  features = defaultFeatures,
  enable,
}) {
  const creds = requireCreds({ storeId, email });
  return request(
    api.post(`${API_BASE_URL}api/pos-subscription/set-feature-auto-renew`, {
      ...creds,
      features,
      enable,
    })
  ); // -> { ok, autoRenew, groupKey }
}

export async function cancelFeatureSubscription({
  storeId,
  email,
  features = defaultFeatures,
  password,
} = {}) {
  const creds = requireCreds({ storeId, email });
  const body = { ...creds, features };
  if (password) body.password = password;
  return request(
    api.post(`${API_BASE_URL}api/pos-subscription/cancel-feature-subscription`, body)
  ); // -> { ok, message, endsOn, groupKey }
}

export async function startFeatureSubscriptionWithProvider({
  storeId,
  email,
  providerPackageId,
  providerPackageOptionId,
  autoRenew = true,
  features = defaultFeatures,
  lang = "en",
}) {
  const creds = requireCreds({ storeId, email });
  return request(
    api.post(`${API_BASE_URL}api/pos-subscription/start-feature-subscription`, {
      ...creds,
      features,
      autoRenew,
      providerPackageId,
      providerPackageOptionId,
      lang,
    })
  ); // -> { url, cardSaved }
}

// Public packages API (live) — used for the Upgrade drawer
export async function listPosInventoryPackages() {
  return request(
    api.get(`${API_BASE_URL}api/pos-subscription/provider-packages`, { timeout: 15000 })
  ); // -> []
}


// ---- helpers ----
function currentLang() {
  try {
    if (window?.APP_LANG) return window.APP_LANG;
    const ls = localStorage.getItem("lang");
    if (ls) return ls;
  } catch(err) {
    console.error("Error getting currentLang", err);
  }
  return "en";
}

function t(key, fallback) {
  try {
    if (typeof window?.translate === "function") {
      const out = window.translate(key);
      if (out && typeof out === "string") return out;
    }
  } catch(err) {
    console.error("Error getting t", err);
  }
  return fallback ?? key;
}

function showSubscriptionToast(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  const headers = err?.response?.headers || {};
  const hdrActive = headers["x-subscription-active"];
  const hdrReason = headers["x-subscription-lock-reason"];

  const isExpired =
    status === 403 &&
    (data.code === "SUBSCRIPTION_EXPIRED" ||
      hdrActive === "false" ||
      hdrReason === "subscription_expired");

  if (isExpired) {
    const defaultMsg = t(
      "Subscription expired read-only",
      "Your POS is in read-only mode because the subscription has expired. Please renew to add, update, or delete."
    );
    // Prefer backend message if present, fallback to our translation key
    const msg = data.message || defaultMsg;
    toast.error(msg, { toastId: "subscription-expired" });
  } else if (status === 403 && data.code === "SUBSCRIPTION_UNKNOWN_TENANT") {
    toast.error(
      t("Unable to verify subscription", "Unable to verify subscription. Operation is blocked."),
      { toastId: "subscription-unknown-tenant" }
    );
  }
}

// Attach language to every request so backend chooses EN/AR for its message
api.interceptors.request.use((config) => {
  const lang = currentLang().toLowerCase().startsWith("ar") ? "ar" : "en";
  config.headers["Accept-Language"] = lang;
  return config;
});

// Global error handler (fires for ALL API modules)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    showSubscriptionToast(err);

    // Optional: show your generic toasts based on route patterns
    const url = err?.config?.url || "";
    if (/get-latest-feature-subscription/.test(url)) {
      toast.error(t("Failed to load subscription", "Failed to load subscription"), {
        toastId: "err-load-subscription",
      });
    } else if (/provider-packages/.test(url)) {
      toast.error(t("Failed to load packages", "Failed to load packages"), {
        toastId: "err-load-packages",
      });
    } else if (/start-feature-subscription/.test(url)) {
      toast.error(t("Failed to start checkout", "Failed to start checkout"), {
        toastId: "err-start-checkout",
      });
    }

    return Promise.reject(err);
  }
);